import React, { createRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { Loader, Card, Button } from '@tucar/pit-stop';
import { useLazyQuery, useReactiveVar, useQuery } from '@apollo/client';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { MapContextProvider } from './mapContext';
import GET_CHARGING_STATIONS from '../../../../graphql/querys/map/getChargingStations';
import GET_CHARGING_STATION from '../../../../graphql/querys/map/getChargingStation';
import { getDriverStore } from '../../../../graphql/store';
import GET_CHARGING_SESSIONS from '../../../../graphql/querys/map/getChargingSessions';
import { renderMapMarkers } from './auxFunctions';
import EmptyView from '../../../../components/extras/EmptyView';
import MapSelection from './mapSelection';
import LocationPin from './pinsSVG/locationPin.svg';
import QRScanner from './qrScanner';
import AlertBanner from './alertBanner';

const ChargersMap = ({ params }) => {
  const [handlePageChange] = useOutletContext();
  const navigate = useNavigate();
  const { rent } = useReactiveVar(getDriverStore);
  const [getStations, { loading, data, error }] = useLazyQuery(GET_CHARGING_STATIONS.query, {
    ...GET_CHARGING_STATIONS.policies,
    variables: { ...params },
  });
  const [scannedConnectorId, setScannedConnectorId] = useState(null);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
  });

  const [chargerToFocus, setChargerToFocus] = useState(null);
  const mapRef = createRef();
  const [zoom, setZoom] = useState(13);
  const [center, setCenter] = useState({
    lat: -33.4066744,
    lng: -70.5762731,
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [markers, setMarkers] = useState(null);
  const [carStatusFilterOptions, setCarStatusFilterOptions] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isActiveSession, setIsActiveSession] = useState(false);

  const { data: stationData, error: stationError } = useQuery(GET_CHARGING_STATION.query, {
    ...GET_CHARGING_STATION.policies,
    variables: { providerConnectorId: scannedConnectorId },
    skip: !scannedConnectorId,
    pollInterval: 10000,
  });

  const handleCodeConfirm = async (code) => {
    setIsScannerOpen(false);
    if (!code) {
      setErrorMessage('El código QR no es válido.');
      return;
    }
    setScannedConnectorId(code);
  };

  const { data: sessionData } = useQuery(GET_CHARGING_SESSIONS.query, {
    ...GET_CHARGING_SESSIONS.policies,
    variables: {
      active: true,
      plate: rent?.carId,
      limit: 1,
    },
    skip: !rent?.carId,
  });

  useEffect(() => {
    const activeSession = sessionData?.getChargingSessions?.instances?.[0];

    if (activeSession) {
      setIsActiveSession(true);
    }
  }, [sessionData]);

  const handleFocus = useCallback((carData, location) => {
    setChargerToFocus(carData);
    if (location) {
      setCenter(location);
    }
  }, []);

  useEffect(() => {
    if (stationError) {
      setErrorMessage(`Error al buscar la estación: ${stationError.message}`);
      setScannedConnectorId(null);
      return;
    }

    if (stationData) {
      const foundStation = stationData?.getChargingStation;

      if (foundStation) {
        const foundConnector = foundStation.connectors.find(
          (conn) => conn.connectorProviderId === scannedConnectorId,
        );

        if (foundConnector) {
          setChargerToFocus(foundStation);
          setSelectedPin(foundConnector);
        } else {
          setErrorMessage('No se encontró el conector dentro de la estación.');
          setScannedConnectorId(null);
        }
      } else {
        setErrorMessage('No se encontró una estación con ese código QR.');
        setScannedConnectorId(null);
      }
    }
  }, [stationData, stationError, scannedConnectorId]);

  const handleZoomChanged = useCallback(() => {
    const newZoom = mapRef.current.getZoom();
    setZoom(newZoom);
  }, [mapRef]);

  const onMapLoad = () => {
    navigator?.geolocation.getCurrentPosition(({ coords: { latitude: lat, longitude: lng } }) => {
      const pos = { lat, lng };
      setCurrentLocation(pos);
      setCenter(pos);
    });
  };

  useEffect(() => {
    handlePageChange(false, true);
    getStations({ variables: { ...params } });
  }, []);

  useEffect(() => {
    getStations({ variables: { ...params } });
  }, [params]);

  useEffect(() => {
    if (!loading && data) {
      setMarkers(data?.getChargingStations || []);
    }
  }, [loading]);

  if (error) {
    return (
      <Box sx={{ height: '100vh' }}>
        <EmptyView />
      </Box>
    );
  }

  if (!isLoaded || (loading && !markers)) {
    return (
      <Box sx={{ height: '100vh' }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Loader size={32} pointRadio="4px" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isScannerOpen && (
        <QRScanner
          onClose={() => setIsScannerOpen(false)}
          onConfirm={handleCodeConfirm}
          errorInScan={setErrorMessage}
        />
      )}
      <MapSelection
        data={chargerToFocus}
        onClose={() => {
          handleFocus(null);
          setSelectedPin(null);
        }}
        selectedPin={selectedPin}
        onPinSelect={setSelectedPin}
        deleteCode={() => setScannedConnectorId(null)}
        isActiveSession={isActiveSession}
        openScanner={() => setIsScannerOpen(true)}
        isRentActive={!!rent?.carId}
      />

      <MapContextProvider
        carToFocus={chargerToFocus}
        focusCar={handleFocus}
        carStatusFilter={{
          filterOptions: carStatusFilterOptions,
          setFilterOptions: setCarStatusFilterOptions,
        }}
      >
        <Box>
          <GoogleMap
            mapContainerStyle={{ height: '100vh' }}
            center={center}
            zoom={zoom}
            options={{
              minZoom: 2,
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
              zoomControl: false,
            }}
            onLoad={(map) => {
              mapRef.current = map;
              map.addListener('zoom_changed', handleZoomChanged);
              onMapLoad(map);
            }}
          >
            {renderMapMarkers(markers)}
            {currentLocation && (
              <Marker
                position={currentLocation}
                icon={{
                  url: LocationPin,
                  scale: 7,
                }}
              />
            )}
          </GoogleMap>
          {isActiveSession && (
            <Box
              sx={{
                position: 'fixed',
                display: 'flex',
                direction: 'row',
                width: '100%',
                justifyContent: 'center',
                bottom: '150px',
                zIndex: 1,
              }}
            >
              <Button onClick={() => navigate('/app/v2/chargers/charging-view')}>
                Gestionar carga
              </Button>
            </Box>
          )}
          {rent?.carId && (
            <Card
              onClick={() => setIsScannerOpen(true)}
              style={{
                position: 'fixed',
                bottom: '100px',
                right: '10px',
                zIndex: 1,
                padding: '8px',
              }}
            >
              <QrCodeIcon sx={{ fontSize: 24 }} />
            </Card>
          )}
        </Box>
      </MapContextProvider>
      {errorMessage && (
        <AlertBanner
          open={!!errorMessage}
          message={errorMessage}
          severity="error"
          onClose={() => setErrorMessage('')}
        />
      )}
    </Box>
  );
};

ChargersMap.propTypes = {
  params: PropTypes.shape({}),
};

ChargersMap.defaultProps = {
  params: {},
};

export default ChargersMap;
